import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Loader2, Plus } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Input } from '@/app/components/ui/input';
import { usePermissions } from '@/hooks/use-permissions';
import { cn } from '@/app/components/ui/utils';
import { AddLocationDialog } from '@/components/shared/add-location-dialog';

export interface LocationOption {
  id: number;
  name: string;
  type: string;
}

interface LocationSearchComboBoxProps {
  value: LocationOption | null;
  onChange: (location: LocationOption | null) => void;
  className?: string;
}

const LOCATION_TYPE_LABELS: Record<string, string> = {
  job_site: 'Project Site',
  client_facility: 'Client Facility',
  depot: 'Depot',
  office: 'Office',
  client_hq: 'Client HQ',
  vehicle: 'Vehicle',
  other: 'Other',
};

function formatLocationType(type: string): string {
  return LOCATION_TYPE_LABELS[type] ?? type;
}

export function LocationSearchComboBox({ value, onChange, className }: LocationSearchComboBoxProps) {
  const { hasMinRole } = usePermissions();
  const canCreateLocation = hasMinRole('Manager');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [addLocationOpen, setAddLocationOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchEnabled = debouncedSearch.length >= 2 && !value;
  const { data, isFetching } = useQuery({
    queryKey: ['locations', 'search', debouncedSearch],
    queryFn: async () => {
      const res = await apiClient.get<{
        results: {
          id: number;
          name: string;
          type: string;
        }[];
      }>('/api/v1/cf-locations/', {
        params: { search: debouncedSearch, page_size: 10 },
      });
      return res.data;
    },
    enabled: searchEnabled,
  });

  const results = data?.results ?? [];

  const handleSelect = (location: LocationOption) => {
    onChange(location);
    setSearch('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSearch('');
  };

  if (value) {
    return (
      <div className={cn('flex items-center gap-3 mt-1 px-3 py-2 border rounded-md bg-gray-50', className)}>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">
            {value.name}
            <span className="text-gray-500 font-normal"> ({formatLocationType(value.type)})</span>
          </p>
        </div>
        <button
          type="button"
          onClick={handleClear}
          className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600"
          aria-label="Clear location selection"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn('mt-1 space-y-1 relative', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <Input
          placeholder="Search locations by name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="pl-10"
        />
      </div>
      {isOpen && search.length >= 2 && (
        <>
          {(isFetching || search !== debouncedSearch) && (
            <p className="text-xs text-gray-400 px-1 py-1 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Searching...
            </p>
          )}
          {!isFetching && search === debouncedSearch && results.length > 0 && (
            <div className="border rounded-md divide-y max-h-48 overflow-y-auto shadow-sm">
              {results.map((location) => (
                <button
                  key={location.id}
                  type="button"
                  className="w-full flex items-start gap-3 px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors"
                  onClick={() => handleSelect(location)}
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {location.name}
                      <span className="text-gray-500 font-normal"> ({formatLocationType(location.type)})</span>
                    </p>
                  </div>
                </button>
              ))}
              {canCreateLocation && (
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 transition-colors"
                  onClick={() => setAddLocationOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  Add Location
                </button>
              )}
            </div>
          )}
          {!isFetching && search === debouncedSearch && results.length === 0 && (
            <div className="border rounded-md shadow-sm">
              <p className="text-xs text-gray-500 px-3 py-2">No locations found.</p>
              {canCreateLocation && (
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 transition-colors border-t"
                  onClick={() => setAddLocationOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  Add Location
                </button>
              )}
            </div>
          )}
        </>
      )}
      {isOpen && search.length > 0 && search.length < 2 && (
        <p className="text-xs text-gray-400 px-1">Type at least 2 characters to search</p>
      )}
      <AddLocationDialog
        open={addLocationOpen}
        onOpenChange={setAddLocationOpen}
        onCreated={handleSelect}
      />
    </div>
  );
}
